/**
 * website.js — Website AI prompt tasks.
 * Schema and quality bar: see _exemplar.js. Template syntax: lib/ai-lab/renderTemplate.js.
 */
export const WEBSITE_TASKS = [
  // ── SITEMAP & PAGE PLAN ────────────────────────────────────────────────────
  {
    id: "website-sitemap",
    category: "website",
    title: "Create Sitemap & Page Plan",
    description: "A complete sitemap with every page's purpose, key sections, primary CTA and navigation structure — ready to hand to a builder or paste into Wix / Shopify.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "stage", label: "Business stage", type: "select", options: ["Idea / pre-launch", "Launched — DTC only", "In local / regional retail", "In national retail", "Scaling / raising capital"], default: "Launched — DTC only", brandKey: "stage" },
      { id: "platform", label: "Website platform", type: "select", options: ["Shopify", "Wix", "Lovable", "Framer", "Other"], default: "Shopify" },
      { id: "siteGoal", label: "Primary site goal", type: "select", options: ["Sell direct (DTC store)", "Build a pre-launch waitlist", "Brand site + store locator (retail-first)", "Support wholesale & retail buyers", "Mix of DTC and retail support"], default: "Sell direct (DTC store)" },
      { id: "skuCount", label: "Number of products / SKUs", type: "select", options: ["1", "2–3", "4–6", "7–12", "13+"], default: "2–3" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Bloated nav; blog-first structure; enterprise feel", brandKey: "avoid" },
    ],
    template: `Act as a senior e-commerce strategist and UX architect who has planned 50+ DTC food and beverage websites on Shopify, Wix and Framer.

Create a complete sitemap and page plan for {{brandName}}, {{category|article}} brand.

PRODUCT
{{productDescription}}
SKU count: {{skuCount}}

TARGET CONSUMER
{{targetConsumer}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

BUSINESS CONTEXT
- Stage: {{stage}}
- Primary site goal: {{siteGoal}}
- Platform: {{platform}}
{{#if channels}}
- Sales channels: {{channels|join}}
{{/if}}

{{#if siteGoal=Build a pre-launch waitlist}}
The site must capture emails above the fold on every page and make the launch timeline clear. Keep the page count minimal.
{{/if}}
{{#if siteGoal=Brand site + store locator (retail-first)}}
The store locator and "Where to buy" path is the primary conversion. Treat online ordering as secondary.
{{/if}}
{{#if siteGoal=Support wholesale & retail buyers}}
Include a wholesale / buyer page with a sell-sheet download and inquiry form, separate from the consumer journey.
{{/if}}
{{#if platform=Shopify}}
Use Shopify's native structure (Home, Collections, Products, Pages, Blog) and name pages the way Shopify's theme editor expects.
{{/if}}
{{#if platform=Wix}}
Structure the plan so it can be pasted into Wix AI site generation as a site description, with each page listed as a separate line.
{{/if}}
{{#if platform=Lovable}}
Describe pages as routes (for example /, /shop, /about) with the components each route needs, so the plan can be used as a Lovable build brief.
{{/if}}
{{#if platform=Framer}}
Favor a lean, design-forward page set with CMS collections for products and blog posts.
{{/if}}

PROVIDE
1. Sitemap as an indented tree (primary nav, secondary nav, footer links, utility links)
2. A table with one row per page: Page name | URL slug | Purpose (one line) | Key sections in order | Primary CTA | Secondary CTA
3. Homepage section order with a one-line rationale per section
4. Product page structure (sections in order) that works for all {{skuCount}} SKU(s)
5. Navigation recommendation: max 5 top-level items, what goes in the footer, and why
6. Conversion path: the 3 most common visitor journeys for {{targetConsumer}} and which pages each touches
7. Pages to deliberately NOT build at the {{stage}} stage and what to use instead
8. Trust elements to place site-wide (reviews, press, guarantees, certifications) — mark any that need proof the founder must supply

{{#if avoid}}
AVOID
{{avoid|list}}
- More than 5 top-level navigation items
- Pages with no clear CTA
{{/if}}
{{#unless avoid}}
AVOID
- More than 5 top-level navigation items
- Pages with no clear CTA
- Enterprise-style mega menus
{{/unless}}

Every page must earn its place: if a page does not help {{targetConsumer}} buy, trust or find {{brandName}}, cut it.`,
    tips: [
      "Paste the page table into the Write Homepage Copy and Write Product Page tasks next — they follow the same section order.",
      "Follow up: “Turn the sitemap into a 2-week build checklist for one founder with no developer.”",
    ],
  },

  // ── HOMEPAGE COPY ──────────────────────────────────────────────────────────
  {
    id: "website-homepage-copy",
    category: "website",
    title: "Write Homepage Copy",
    description: "Section-by-section homepage copy with headlines, body text, CTAs and word counts — ready to paste into your page builder.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "3 min to fill · 2 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", required: true, brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine", brandKey: "ingredients" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences. No hype words.", brandKey: "brandVoice" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "founderStory", label: "Founder story (short)", type: "textarea", placeholder: "2–4 sentences on why you started the brand", brandKey: "founderStory", span: "full" },
      { id: "pricePoint", label: "Retail price point", type: "text", placeholder: "$2.99 per can", brandKey: "pricePoint" },
      { id: "primaryCta", label: "Primary call to action", type: "select", options: ["Shop Now", "Subscribe & Save", "Try the Starter Pack", "Join the Waitlist", "Find a Store Near You", "Build Your Bundle"], default: "Shop Now" },
      { id: "proofPoints", label: "Proof you can show (optional)", type: "textarea", placeholder: "4.8 stars from 312 reviews; featured in Men's Health; sold in 40 Sprouts stores", span: "full" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Hype words; 'game-changing'; exclamation points", brandKey: "avoid" },
    ],
    template: `Act as a senior DTC conversion copywriter who has written homepages for breakout {{category|lower}} and better-for-you CPG brands.

Write complete homepage copy for {{brandName}}, {{category|article}} for {{targetConsumer}}.

PRODUCT
{{productDescription}}
{{#if pricePoint}}
Price: {{pricePoint}}
{{/if}}

BRAND POSITIONING
{{positioning}}

{{#if claims}}
KEY CLAIMS (use only these — do not invent benefits)
{{claims|list}}
{{/if}}

{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|join}}
{{/if}}

VOICE
{{#if brandVoice}}
{{brandVoice}}
{{/if}}
{{#if personality}}
Personality: {{personality|join}}.
{{/if}}
{{#unless brandVoice}}
Clear, confident, specific. Short sentences. Benefits before features.
{{/unless}}

{{#if proofPoints}}
PROOF POINTS (real — use these exactly)
{{proofPoints}}
{{/if}}
{{#unless proofPoints}}
PROOF POINTS
None supplied. Write the proof section with clearly marked [PLACEHOLDER] slots for review count, press logos and retailer names — never invent numbers, quotes or publications.
{{/unless}}

PRIMARY CTA: "{{primaryCta}}" (use this exact phrase on every primary button)

WRITE EACH SECTION IN ORDER
1. HERO — H1 (max 8 words), H2 supporting line (max 20 words), primary CTA button, one line of micro-proof under the button. Provide 3 H1 options and mark the recommended one.
2. BENEFIT BAR — 3 to 4 benefit icons with 2 to 4 word labels
3. WHY {{brandName|upper}} — 3 benefit blocks, each: headline (max 6 words) + 35 to 50 words body
4. SOCIAL PROOF — section headline + 3 review-style testimonials (if no real proof supplied, write them as clearly labeled [PLACEHOLDER — replace with real reviews])
5. HOW IT WORKS / WHEN TO USE — 3 steps or 3 moments, max 25 words each
6. INGREDIENTS / WHAT'S INSIDE — intro line + one line per hero ingredient on what it does, in plain language
7. FOUNDER NOTE — 80 to 120 words in first person{{#if founderStory}}, based on: {{founderStory}}{{/if}}
8. FAQ TEASER — 4 questions with 1 to 2 sentence answers
9. FINAL CTA — headline, one supporting line, primary CTA button
10. SEO title tag (max 60 characters) and meta description (max 155 characters)

FORMAT
Label each section, give word counts, and put all button text in quotes. Total body copy under 700 words.

{{#if avoid}}
AVOID
{{avoid|list}}
- Health or disease claims not listed above — flag anything that needs regulatory review
- Exclamation points and hype adjectives
{{/if}}
{{#unless avoid}}
AVOID
- Health or disease claims not listed above — flag anything that needs regulatory review
- Exclamation points and hype adjectives ("game-changing", "revolutionary")
- Generic {{category|lower}} clichés
{{/unless}}

Finish with 3 specific suggestions for images or video to pair with the hero and benefit sections.`,
    tips: [
      "Paste the output into Claude or ChatGPT, then ask: “Tighten every section by 20% and give me a mobile-first version of the hero.”",
      "Use the section labels to match the page order from your Sitemap & Page Plan.",
    ],
  },

  // ── PRODUCT PAGE ───────────────────────────────────────────────────────────
  {
    id: "website-product-page",
    category: "website",
    title: "Write Product Page",
    description: "Complete product page copy — title, descriptions, benefit bullets, ingredient notes, usage, shipping and subscription copy, FAQs and SEO metadata — with regulatory flags.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "shopify"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productName", label: "Product / SKU name", type: "text", placeholder: "Citrus Rush 12-pack", required: true },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine", brandKey: "ingredients" },
      { id: "pricePoint", label: "Retail price", type: "text", placeholder: "$2.99 per can / $35.88 per 12-pack", brandKey: "pricePoint" },
      { id: "flavors", label: "Flavors / variants", type: "tags", placeholder: "Citrus Rush, Berry Wave, Tropical" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "subscriptionOffer", label: "Subscription offer", type: "select", options: ["No subscription", "Subscribe & Save 10%", "Subscribe & Save 15%", "Subscribe & Save 20%", "Subscription with free shipping"], default: "Subscribe & Save 15%" },
      { id: "shippingNotes", label: "Shipping details", type: "text", placeholder: "Free shipping over $50; ships in 1–2 business days; U.S. only" },
      { id: "platform", label: "Website platform", type: "select", options: ["Shopify", "Wix", "Lovable", "Framer", "Other"], default: "Shopify" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Medical claims; 'clinically proven'; supplement-style language", brandKey: "avoid" },
    ],
    template: `Act as a senior e-commerce copywriter and CPG regulatory-aware marketer who writes high-converting product pages for food and beverage brands.

Write the full product page for "{{productName}}" by {{brandName}}, {{category|article}}.

PRODUCT
{{productDescription}}
{{#if flavors}}
Flavors / variants: {{flavors|join}}
{{/if}}
{{#if pricePoint}}
Price: {{pricePoint}}
{{/if}}

{{#if targetConsumer}}
TARGET CONSUMER
{{targetConsumer}}
{{/if}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if claims}}
APPROVED CLAIMS (use only these)
{{claims|list}}
{{/if}}

{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|join}}
{{/if}}

{{#if brandVoice}}
VOICE
{{brandVoice}}
{{/if}}

COMMERCIAL DETAILS
- Subscription: {{subscriptionOffer}}
- Shipping: {{shippingNotes|default:not supplied — write the shipping block with [PLACEHOLDER] values}}
- Platform: {{platform}}

PROVIDE
1. Product title (max 60 characters) — 3 options, mark the recommended one
2. Short description (40 to 60 words) for above the fold
3. Benefit bullets — 5 bullets, each under 12 words, benefits before features
4. Long description (150 to 220 words) with subheads, written for {{#if targetConsumer}}{{targetConsumer}}{{/if}}{{#unless targetConsumer}}the core shopper{{/unless}}
5. "What's inside" — one plain-language line per hero ingredient explaining what it does
6. Nutrition / ingredient notes block — instructions for what the founder must paste from the actual label (do not invent nutrition facts)
7. How to use / when to enjoy — 3 moments, max 20 words each
{{#if subscriptionOffer!=No subscription}}
8. Subscription copy — headline, 3 reasons to subscribe, and the exact "{{subscriptionOffer}}" offer line for the purchase widget
{{/if}}
{{#if subscriptionOffer=No subscription}}
8. One-time purchase nudge — a line that encourages the multi-pack or bundle
{{/if}}
9. Shipping & returns block (2 to 3 sentences)
10. Product FAQs — 6 questions with 1 to 3 sentence answers
11. SEO title tag (max 60 characters), meta description (max 155 characters), URL slug, and image alt text for 3 images
12. A list of which fields or page blocks each item maps to in {{platform}}{{#if platform=Shopify}} (Title, Description, Metafields, SEO section){{/if}}

REGULATORY
Flag every sentence that makes a health, nutrient-content, structure/function or "natural" claim with [REVIEW] so the founder can confirm it with a regulatory advisor. Do not write disease claims. Do not invent certifications, awards, studies or percentages.

{{#if avoid}}
AVOID
{{avoid|list}}
- Hype adjectives and exclamation points
{{/if}}
{{#unless avoid}}
AVOID
- Hype adjectives and exclamation points
- Supplement-style language ("clinically proven", "formulated to")
- Claims not in the approved list
{{/unless}}

Format every block with a clear label so it can be pasted into {{platform}} section by section.`,
    tips: [
      "Paste the blocks into Shopify, then use Shopify Magic to spin the other flavor variants.",
      "Follow up: “Write the same page for [next flavor] — keep structure identical, change only flavor-specific lines.”",
    ],
  },

  // ── FAQS ───────────────────────────────────────────────────────────────────
  {
    id: "website-faqs",
    category: "website",
    title: "Write FAQs",
    description: "20 grouped FAQs (product, ingredients, shipping, subscription, wholesale) written in your brand voice with regulatory flags where needed.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine", brandKey: "ingredients" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "channels", label: "Sales channels", type: "tags", placeholder: "DTC website, Amazon, Natural grocery", brandKey: "channels" },
      { id: "shippingNotes", label: "Shipping details", type: "text", placeholder: "Free shipping over $50; U.S. only; ships in 1–2 days" },
      { id: "subscriptionOffer", label: "Subscription offer", type: "select", options: ["No subscription", "Subscribe & Save 10%", "Subscribe & Save 15%", "Subscribe & Save 20%", "Subscription with free shipping"], default: "Subscribe & Save 15%" },
      { id: "wholesaleStatus", label: "Wholesale status", type: "select", options: ["Not offering wholesale yet", "Accepting wholesale inquiries directly", "Available through a distributor", "Available on Faire / wholesale marketplace"], default: "Accepting wholesale inquiries directly" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Medical claims; corporate tone; vague answers", brandKey: "avoid" },
    ],
    template: `Act as a senior e-commerce customer-experience lead and copywriter for food and beverage CPG brands.

Write 20 FAQs for the {{brandName}} website. {{brandName}} is {{category|article}}.

PRODUCT
{{productDescription}}

{{#if targetConsumer}}
WHO IS ASKING
{{targetConsumer}}
{{/if}}

{{#if claims}}
APPROVED CLAIMS (answers may reference only these)
{{claims|list}}
{{/if}}

{{#if ingredients}}
HERO INGREDIENTS
{{ingredients|join}}
{{/if}}

OPERATIONAL FACTS
- Subscription: {{subscriptionOffer}}
- Wholesale: {{wholesaleStatus}}
{{#if shippingNotes}}
- Shipping: {{shippingNotes}}
{{/if}}
{{#unless shippingNotes}}
- Shipping: not supplied — answer shipping questions with [PLACEHOLDER] values the founder fills in
{{/unless}}
{{#if channels}}
- Where sold: {{channels|join}}
{{/if}}

{{#if brandVoice}}
VOICE
{{brandVoice}}
{{/if}}
{{#unless brandVoice}}
VOICE
Friendly, direct, human. Answer the question in the first sentence.
{{/unless}}

PROVIDE
Group the 20 FAQs under these headers, in this order:
1. PRODUCT (5 questions) — what it is, who it is for, taste, how to use, how it compares to typical {{category|lower}} options
2. INGREDIENTS & NUTRITION (5 questions) — what's inside, what's not, allergens, sourcing, caffeine / sugar / calories as relevant
3. SHIPPING & ORDERS (4 questions) — timing, cost, regions, damaged or wrong orders
4. SUBSCRIPTION (3 questions) — how it works, how to pause / cancel, what the discount is{{#if subscriptionOffer=No subscription}} (write these as "Do you offer a subscription?" style answers that point to multi-packs){{/if}}
5. WHOLESALE & RETAIL (3 questions) — how stores can carry it, minimums, where to find it in stores

FORMAT
- Each answer 1 to 3 sentences, max 60 words
- Write questions the way a real shopper would type them
- After the 20 FAQs, add a short table: Question | Page where it should also appear (product page, cart, checkout, footer)
- Provide FAQ schema markup (JSON-LD) for the first 10 questions so they can be indexed by search engines

REGULATORY
Mark any answer that touches health effects, allergens, nutrient content or "natural" language with [REVIEW] for regulatory check. Never invent nutrition numbers, certifications or test results — use [PLACEHOLDER] where a fact is missing.

{{#if avoid}}
AVOID
{{avoid|list}}
- Corporate or legal-sounding tone
- Answers that dodge the question
{{/if}}
{{#unless avoid}}
AVOID
- Corporate or legal-sounding tone
- Answers that dodge the question
- Repeating the question in the answer
{{/unless}}`,
    tips: [
      "Paste the first 4 product FAQs into your product page and the rest into a dedicated /faq page.",
      "Follow up: “Rewrite the shipping answers for international customers” or “Add 5 FAQs about [specific concern].”",
    ],
  },

  // ── FOUNDER STORY / ABOUT PAGE ─────────────────────────────────────────────
  {
    id: "website-founder-story",
    category: "website",
    title: "Write Founder Story / About Page",
    description: "A polished About page built from your founder story — headline, narrative, mission, values and a closing CTA — in your brand voice.",
    outputType: "text",
    recommendedTools: ["claude", "chatgpt", "gemini"],
    estimatedTime: "2 min to fill · 1 min in Claude",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "founderStory", label: "Founder story (raw notes are fine)", type: "textarea", placeholder: "Why you started it, the moment it clicked, what you tried before, what you believe", required: true, brandKey: "founderStory", span: "full" },
      { id: "founderName", label: "Founder name(s)", type: "text", placeholder: "Jordan Lee and Sam Ortiz" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "tone", label: "Story tone", type: "select", options: ["Warm and personal", "Mission-driven", "Straight-talking", "Witty and light", "Premium and understated"], default: "Warm and personal" },
      { id: "pageLength", label: "Page length", type: "select", options: ["Short (about 300 words)", "Standard (about 500 words)", "Long (about 800 words)"], default: "Standard (about 500 words)" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Cliché 'we couldn't find X so we made it'; over-sharing; savior language", brandKey: "avoid" },
    ],
    template: `Act as a senior brand storyteller and copywriter who has written About pages for emerging food and beverage brands that went on to national retail.

Write the About / Founder Story page for {{brandName}}, {{category|article}}.

FOUNDER STORY (raw source — keep every fact, improve the telling)
{{founderStory}}
{{#if founderName}}
Founder(s): {{founderName}}
{{/if}}

{{#if productDescription}}
PRODUCT
{{productDescription}}
{{/if}}

{{#if targetConsumer}}
READER
{{targetConsumer}} — they want to know who is behind the brand and whether they can trust it.
{{/if}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

VOICE
Tone: {{tone}}
{{#if brandVoice}}
{{brandVoice}}
{{/if}}
{{#if personality}}
Personality: {{personality|join}}.
{{/if}}

LENGTH
{{pageLength}} of body copy, excluding headers.

PROVIDE
1. Page headline (max 8 words) — 3 options, mark the recommended one
2. Opening hook (1 to 2 sentences) — the specific moment or problem, not the company
3. The story — in 3 to 4 short sections with subheads: the problem, the turning point, what we built, what we believe. First person{{#if founderName}} from {{founderName}}{{/if}}.
4. Mission statement (one sentence, under 20 words)
5. 3 brand values — each a 2 to 4 word label plus one sentence of what it means in practice
6. "Why {{brandName}} is different" — 3 short proof-style lines tied to the product, not adjectives
7. Closing paragraph and CTA (shop, subscribe or follow) that fits the {{tone|lower}} tone
8. 2 pull quotes (under 15 words each) for design callouts
9. Image suggestions — 4 photo ideas for the page (founder, process, product, community)
10. A 50-word short version for the homepage founder section and a 25-word version for social bios

RULES
- Use only facts from the founder story above. Where a detail is missing (date, city, number), insert [ADD DETAIL] rather than inventing it.
- No health or efficacy claims in the story.

{{#if avoid}}
AVOID
{{avoid|list}}
- The "we couldn't find one, so we made it" opener
- Savior or guru language
{{/if}}
{{#unless avoid}}
AVOID
- The "we couldn't find one, so we made it" opener
- Savior or guru language
- Generic statements that any brand could say
{{/unless}}`,
    tips: [
      "Paste into Claude for the best long-form voice. Then ask: “Make it 30% shorter and more concrete — replace every adjective with a detail.”",
      "Use the 25-word version for your Instagram bio and the 50-word version on the homepage.",
    ],
  },

  // ── SEO CONTENT PLAN ───────────────────────────────────────────────────────
  {
    id: "website-seo-content",
    category: "website",
    title: "Create SEO Content Plan",
    description: "Keyword clusters, 12 blog titles with outlines, and an internal-linking map built around your product and the questions your consumer actually searches.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "perplexity"],
    estimatedTime: "2 min to fill · 2 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "ingredients", label: "Hero ingredients", type: "tags", placeholder: "Green coffee extract, L-theanine", brandKey: "ingredients" },
      { id: "competitors", label: "Competitors", type: "tags", placeholder: "Celsius, Alani Nu, Ghost", brandKey: "competitors" },
      { id: "website", label: "Website", type: "text", placeholder: "https://", brandKey: "website" },
      { id: "seedKeywords", label: "Seed keywords (optional)", type: "tags", placeholder: "natural energy drink, clean caffeine, pre-workout drink" },
      { id: "contentCadence", label: "Publishing cadence", type: "select", options: ["2 posts per month", "4 posts per month", "8 posts per month"], default: "4 posts per month" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Medical advice topics; listicles with no point of view", brandKey: "avoid" },
    ],
    template: `Act as a senior SEO content strategist who builds organic search programs for DTC food and beverage brands.

Create a 3-month SEO content plan for {{brandName}}, {{category|article}}{{#if website}} at {{website}}{{/if}}.

PRODUCT
{{productDescription}}

TARGET CONSUMER (the searcher)
{{targetConsumer}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if claims}}
KEY CLAIMS
{{claims|join}}
{{/if}}
{{#if ingredients}}
HERO INGREDIENTS (each is a content cluster candidate)
{{ingredients|join}}
{{/if}}

{{#if competitors}}
COMPETITORS
{{competitors|join}}. Include comparison and alternative-seeking queries where appropriate, but keep comparisons factual and verifiable.
{{/if}}

{{#if seedKeywords}}
SEED KEYWORDS
{{seedKeywords|list}}
{{/if}}

CADENCE
{{contentCadence}} for 3 months.

PROVIDE
1. 5 to 6 keyword clusters — table: Cluster | Search intent (informational / commercial / comparison / brand) | 6 to 10 example queries | Page type that should rank (product, collection, blog, FAQ) | Priority (1 to 3) with one-line reason
2. Money pages — which product and collection pages target which commercial keywords, plus a recommended title tag (max 60 characters) and H1 for each
3. 12 blog titles (enough for the first 3 months at {{contentCadence}}), each with: target keyword, intent, 5 to 7 H2 outline, suggested word count, primary product to link to, and one line on why {{brandName}} has the right to rank for it
4. Internal linking map — for each blog post, 2 to 3 internal links (to product, collection, FAQ or other posts) and anchor text
5. Publishing calendar — month by month, in priority order, with the quick wins first
6. 3 link-worthy asset ideas (guides, tools, data) that could earn backlinks in the {{category|lower}} space
7. Measurement — 5 metrics to track monthly and what "working" looks like by month 3

RULES
- Do not invent search volumes or difficulty scores. Label clusters by relative priority and note that the founder should validate volumes in a keyword tool.
- No medical or disease-related topics. Flag any ingredient topic that needs a careful, compliant angle with [REVIEW].

{{#if avoid}}
AVOID
{{avoid|list}}
- Listicles with no point of view
- Topics the brand has no credibility to cover
{{/if}}
{{#unless avoid}}
AVOID
- Listicles with no point of view
- Topics the brand has no credibility to cover
- Keyword-stuffed titles
{{/unless}}`,
    tips: [
      "Run this in Perplexity if you want live competitor URLs and current search trends cited alongside the plan.",
      "Follow up: “Write post #1 in full — 1,200 words, with the H2s above, in our brand voice.”",
    ],
  },

  // ── LANDING PAGE ───────────────────────────────────────────────────────────
  {
    id: "website-landing-page",
    category: "website",
    title: "Write Landing Page",
    description: "Single-CTA landing page copy for a specific offer or campaign — every section, A/B headline variants and a matching ad-to-page message map.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "lovable"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "offerName", label: "Offer / campaign name", type: "text", placeholder: "Starter Pack — 6 cans for $15", required: true },
      { id: "offerDetails", label: "Offer details", type: "textarea", placeholder: "6 cans, 2 of each flavor, free shipping, one per customer, ends March 31", required: true, span: "full" },
      { id: "trafficSource", label: "Where traffic comes from", type: "select", options: ["Meta (Instagram / Facebook) ads", "TikTok ads or creators", "Google search ads", "Influencer / affiliate links", "Email or SMS", "QR code on packaging", "Podcast or audio ad"], default: "Meta (Instagram / Facebook) ads" },
      { id: "primaryCta", label: "Primary call to action", type: "select", options: ["Claim the Offer", "Get the Starter Pack", "Shop Now", "Subscribe & Save", "Join the Waitlist", "Get My Discount"], default: "Get the Starter Pack" },
      { id: "platform", label: "Page builder", type: "select", options: ["Shopify", "Wix", "Lovable", "Framer", "Other"], default: "Shopify" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Fake urgency; countdown timers; health claims", brandKey: "avoid" },
    ],
    template: `Act as a senior direct-response copywriter and CRO specialist for DTC food and beverage brands.

Write a single-offer landing page for {{brandName}} ({{category|article}}) promoting: {{offerName}}.

OFFER DETAILS (use exactly — do not add terms or change numbers)
{{offerDetails}}

PRODUCT
{{productDescription}}

TARGET CONSUMER
{{targetConsumer}}

{{#if positioning}}
BRAND POSITIONING
{{positioning}}
{{/if}}

{{#if claims}}
APPROVED CLAIMS (use only these)
{{claims|list}}
{{/if}}

{{#if brandVoice}}
VOICE
{{brandVoice}}
{{/if}}

TRAFFIC SOURCE: {{trafficSource}}
{{#if trafficSource=Meta (Instagram / Facebook) ads}}
Visitors arrive mid-scroll on mobile with low intent. The hero must restate the ad's promise within 3 seconds and the page must work at thumb speed.
{{/if}}
{{#if trafficSource=TikTok ads or creators}}
Visitors arrive from a creator video. Mirror the creator's language, lead with the product in use, and keep the tone native to TikTok, not corporate.
{{/if}}
{{#if trafficSource=Google search ads}}
Visitors have intent and are comparing. Lead with specifics (what, how much, why better) and put proof high on the page.
{{/if}}
{{#if trafficSource=QR code on packaging}}
Visitors are holding the product. Skip the introduction, reward them for scanning, and drive to the offer immediately.
{{/if}}
{{#if trafficSource=Email or SMS}}
Visitors already know the brand. Keep it short, offer-first, with minimal brand re-introduction.
{{/if}}

ONE CTA ONLY: "{{primaryCta}}" — no navigation, no secondary links except legal footer.

PROVIDE
1. HERO — H1 (max 8 words), H2 (max 18 words), CTA button, micro-proof line. Give 3 A/B headline variants: A = benefit-led, B = offer-led, C = curiosity / pattern-interrupt. Mark the one to launch with.
2. OFFER BLOCK — what's included, price, savings, and the exact terms from the offer details
3. 3 BENEFIT BLOCKS — headline (max 6 words) + 30 to 45 words each
4. PROOF — testimonial section with 3 slots (write as [PLACEHOLDER — real review] unless proof is supplied), plus a "featured in / sold at" bar with placeholder slots
5. HOW IT WORKS — 3 steps, max 15 words each
6. OBJECTION CRUSHER — 4 short FAQs answering the top reasons not to buy right now
7. GUARANTEE / RISK REVERSAL — 2 sentences (mark [CONFIRM POLICY] for the founder)
8. FINAL CTA — headline, one line, button
9. Mobile notes — which sections to collapse or shorten on mobile, and the sticky CTA bar text
10. Message map — a 2-column table: what the {{trafficSource}} creative promises | the line on the page that pays it off
{{#if platform=Lovable}}
11. A short build brief for Lovable: section list in order, component per section, mobile-first, one CTA, no nav
{{/if}}
{{#if platform!=Lovable}}
11. Section-to-block mapping for {{platform}}
{{/if}}

RULES
Total copy under 600 words. No health or efficacy claims beyond the approved list — mark anything borderline [REVIEW]. Urgency must come from the real terms above, never from invented scarcity.

{{#if avoid}}
AVOID
{{avoid|list}}
- Fake countdown timers or invented scarcity
- More than one CTA
{{/if}}
{{#unless avoid}}
AVOID
- Fake countdown timers or invented scarcity
- More than one CTA
- Brand-story detours before the offer
{{/unless}}`,
    tips: [
      "Paste the Lovable build brief (#11) straight into Lovable to get a working page; paste the copy into Shopify or Framer sections otherwise.",
      "Follow up: “Write 3 Meta ad primary texts that match hero variant A so ad and page say the same thing.”",
    ],
  },

  // ── WEBSITE BUILDER PROMPT ─────────────────────────────────────────────────
  {
    id: "website-builder-prompt",
    category: "website",
    title: "Create Website Builder Prompt",
    description: "A paste-ready generation prompt for Wix AI, Lovable, Shopify Magic or Framer AI that describes your brand, pages, style and components so the builder produces an on-brand first draft.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "wix", "lovable", "framer", "shopify"],
    estimatedTime: "3 min to fill · 1 min in ChatGPT · then paste into your builder",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine, 3 flavors", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "style", label: "Desired style", type: "tags", placeholder: "Premium, Modern, Minimal, Bold", brandKey: "visualStyle" },
      { id: "brandColors", label: "Existing brand colors (optional)", type: "tags", placeholder: "#111111, #F4F1EA, #FF5A1F", brandKey: "brandColors" },
      { id: "fonts", label: "Fonts (optional)", type: "tags", placeholder: "Inter, Playfair Display", brandKey: "fonts" },
      { id: "personality", label: "Brand personality", type: "tags", placeholder: "Confident, Clean, Energetic", brandKey: "personality" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "platform", label: "Website builder", type: "select", options: ["Wix AI", "Lovable", "Shopify Magic", "Framer AI", "Other"], default: "Wix AI", required: true },
      { id: "pages", label: "Pages to include", type: "tags", placeholder: "Home, Shop, About, FAQ, Contact", default: "Home, Shop, About, FAQ, Contact" },
      { id: "siteGoal", label: "Primary site goal", type: "select", options: ["Sell direct (DTC store)", "Build a pre-launch waitlist", "Brand site + store locator (retail-first)", "Support wholesale & retail buyers"], default: "Sell direct (DTC store)" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Stock-photo look; purple gradients; template feel", brandKey: "avoid" },
    ],
    template: `Act as a senior web designer and AI prompt engineer who builds DTC food and beverage sites with AI website builders.

Write ONE paste-ready generation prompt that I will paste into {{platform}} to generate the first version of the website for {{brandName}}, {{category|article}}. Output only the final prompt inside a single code block, followed by a short "how to use" note.

THE PROMPT MUST CONTAIN, IN THIS ORDER
1. Brand summary: {{brandName}}, {{category|lower}}, {{#if positioning}}positioned as "{{positioning}}", {{/if}}for {{targetConsumer}}
2. Product: {{productDescription}}
3. Site goal: {{siteGoal}}
4. Pages: {{pages|join}} — for each page, 3 to 6 sections in order with the exact headline to use and one line describing each section's content
5. Visual style: {{#if style}}{{style|join}}{{/if}}{{#unless style}}premium, modern, clean{{/unless}}{{#if brandColors}}; palette {{brandColors|join}} (state which is background, primary, accent){{/if}}{{#unless brandColors}}; propose a 3-color palette with hex codes{{/unless}}{{#if fonts}}; typography {{fonts|join}}{{/if}}{{#unless fonts}}; one display and one body font{{/unless}}
6. Components: sticky header with single primary CTA, hero with product image placeholder, benefit grid, review carousel, product cards, FAQ accordion, email capture, footer with legal and social links
7. Voice for all generated copy: {{#if brandVoice}}{{brandVoice}}{{/if}}{{#unless brandVoice}}clear, confident, specific; short sentences{{/unless}}{{#if personality}} — personality: {{personality|join}}{{/if}}
8. Mobile-first layout, fast loading, accessible contrast, image placeholders labeled with what photo goes there
9. An explicit "do not" list (see AVOID below)

{{#if platform=Wix AI}}
PLATFORM INSTRUCTIONS — WIX AI
Write the prompt as a natural-language site description under 1,500 characters for the "Describe your site" step, then a second, longer block the user pastes into the Wix AI text / section generator page by page. Name the business type as "{{category|lower}} brand / online store". Ask Wix to enable Wix Stores.
{{/if}}
{{#if platform=Lovable}}
PLATFORM INSTRUCTIONS — LOVABLE
Write the prompt as a build brief: tech-agnostic, mobile-first, one route per page ({{pages|join}}), component names per section, a design-token section (colors, fonts, radius, spacing), and "use placeholder images with descriptive alt text". Ask for a single primary CTA per page and a reusable product card. Add a final line: "Build the Home page first and stop for review."
{{/if}}
{{#if platform=Shopify Magic}}
PLATFORM INSTRUCTIONS — SHOPIFY MAGIC
Shopify Magic works field by field, not whole-site. Output a set of short prompts: one for the theme's homepage sections (hero, featured collection, rich text, testimonials, FAQ), one product-description prompt per product, one collection-description prompt, and one for the About page. Each under 400 characters, in the voice above.
{{/if}}
{{#if platform=Framer AI}}
PLATFORM INSTRUCTIONS — FRAMER AI
Write the prompt as a design-led brief: describe layout rhythm, type scale, motion (subtle, purposeful), and section composition. Pages: {{pages|join}}. Request CMS collections for products and FAQs. Keep it under 1,200 characters for the Framer AI start dialog, with a longer per-page block for the section generator.
{{/if}}
{{#if platform=Other}}
PLATFORM INSTRUCTIONS
Write the prompt as a platform-neutral site brief under 2,000 characters, then a per-page section block, so it can be pasted into any AI website builder.
{{/if}}
{{#if siteGoal=Build a pre-launch waitlist}}
The prompt must ask the builder for a lead-capture / waitlist form above the fold on every page and a clear launch-timeline line in the hero.
{{/if}}
{{#if siteGoal=Brand site + store locator (retail-first)}}
The prompt must ask the builder for a "Where to buy" page with a store locator or retailer list as the primary conversion path.
{{/if}}

{{#if avoid}}
AVOID (include as the prompt's "do not" list)
{{avoid|list}}
- Generic stock-photo aesthetic
- Template-looking layouts and purple gradients
{{/if}}
{{#unless avoid}}
AVOID (include as the prompt's "do not" list)
- Generic stock-photo aesthetic
- Template-looking layouts and purple gradients
- More than one primary CTA per page
{{/unless}}

After the code block, add a 4-step "how to use" note: where to paste it in {{platform}}, what to upload first (logo, product photos), what to check after generation, and the first 3 edits to make.`,
    tips: [
      "Upload your logo and product photos to the builder before pasting the generated prompt — it anchors the style.",
      "Then run Write Homepage Copy and paste the real copy over the builder's placeholder text.",
    ],
  },

  // ── WELCOME EMAIL SERIES ───────────────────────────────────────────────────
  {
    id: "website-email-welcome",
    category: "website",
    title: "Write Welcome Email Series",
    description: "A 5-email welcome flow for new subscribers or customers — subject lines, preview text, body copy, CTAs and send timing.",
    outputType: "text",
    recommendedTools: ["chatgpt", "claude", "gemini"],
    estimatedTime: "2 min to fill · 1 min in ChatGPT",
    fields: [
      { id: "brandName", label: "Brand name", type: "text", placeholder: "XYZ", required: true, brandKey: "brandName" },
      { id: "category", label: "Product category", type: "text", placeholder: "Energy drink", required: true, brandKey: "category" },
      { id: "productDescription", label: "Product description", type: "textarea", placeholder: "12 oz sleek can, zero sugar, 200 mg natural caffeine", required: true, brandKey: "productDescription", span: "full" },
      { id: "targetConsumer", label: "Target consumer", type: "textarea", placeholder: "Men and women 18–35 interested in fitness", required: true, brandKey: "targetConsumer", span: "full" },
      { id: "positioning", label: "Brand positioning", type: "textarea", placeholder: "Natural premium performance energy", brandKey: "positioning", span: "full" },
      { id: "claims", label: "Key claims", type: "tags", placeholder: "Zero sugar, Natural caffeine, No artificial colors", brandKey: "claims" },
      { id: "brandVoice", label: "Brand voice", type: "textarea", placeholder: "Direct and encouraging. Short sentences.", brandKey: "brandVoice" },
      { id: "founderStory", label: "Founder story (short)", type: "textarea", placeholder: "2–4 sentences on why you started the brand", brandKey: "founderStory", span: "full" },
      { id: "audienceType", label: "Who receives this series", type: "select", options: ["New email subscribers (have not purchased)", "First-time customers (just ordered)", "Both — write two short variants where they differ"], default: "New email subscribers (have not purchased)" },
      { id: "welcomeOffer", label: "Welcome offer (optional)", type: "text", placeholder: "10% off first order with code WELCOME10" },
      { id: "subscriptionOffer", label: "Subscription offer", type: "select", options: ["No subscription", "Subscribe & Save 10%", "Subscribe & Save 15%", "Subscribe & Save 20%", "Subscription with free shipping"], default: "Subscribe & Save 15%" },
      { id: "avoid", label: "What should AI avoid?", type: "textarea", placeholder: "Discount-heavy tone; long emails; 'Hey there!'", brandKey: "avoid" },
    ],
    template: `Act as a senior email marketing strategist and copywriter for DTC food and beverage brands.

Write a 5-email welcome series for {{brandName}}, {{category|article}}, sent to: {{audienceType}}.

PRODUCT
{{productDescription}}

TARGET CONSUMER
{{targetConsumer}}
{{#if positioning}}
Positioning: {{positioning}}
{{/if}}

{{#if claims}}
APPROVED CLAIMS (use only these)
{{claims|list}}
{{/if}}

VOICE
{{brandVoice|default:Warm, direct, specific. Short paragraphs. One idea per email.}}

{{#if founderStory}}
FOUNDER STORY (for email 2)
{{founderStory}}
{{/if}}

OFFERS
- Welcome offer: {{welcomeOffer|default:none — do not invent a discount; lead with value and product education}}
- Subscription: {{subscriptionOffer}}

SERIES STRUCTURE
{{#if audienceType=First-time customers (just ordered)}}
Email 1 (immediately): thank you + what happens next + how to get the most from the first order
Email 2 (day 2): founder story and why the brand exists
Email 3 (day 5): how to use / best moments for the product + one tip
Email 4 (day 9): social proof and community — reviews, UGC, how to share
Email 5 (day 14): reorder or subscribe
{{/if}}
{{#if audienceType!=First-time customers (just ordered)}}
Email 1 (immediately): welcome + what makes the product different in one line
Email 2 (day 2): founder story and why the brand exists
Email 3 (day 4): product education — ingredients, how it works, how to use
Email 4 (day 7): social proof + objection handling (taste, price, "does it work")
Email 5 (day 10): last nudge before the flow ends
{{/if}}
{{#if welcomeOffer}}
Emails 1 and 5 must carry the welcome offer wording and code exactly as written above.
{{/if}}
{{#if subscriptionOffer!=No subscription}}
Email 5 must present "{{subscriptionOffer}}" as the smart way to buy.
{{/if}}

FOR EACH EMAIL PROVIDE
1. Send timing and goal (one line)
2. Subject line — 3 options (under 45 characters), mark the recommended one
3. Preview text (under 90 characters)
4. Body copy — 90 to 160 words, with a single CTA button text in quotes
5. One P.S. line and an image / GIF suggestion

THEN PROVIDE
6. A summary table: Email | Day | Subject | CTA | Primary metric to watch
7. Exit rules: when someone should leave the flow (purchased, subscribed) and what flow they move to next
8. 2 A/B test ideas for the series

RULES
No health or efficacy claims beyond the approved list — mark anything borderline [REVIEW]. Do not invent reviews, press or numbers; use [PLACEHOLDER] slots.

{{#if avoid}}
AVOID
{{avoid|list}}
- Discount-only messaging
- Emails over 160 words
{{/if}}
{{#unless avoid}}
AVOID
- Discount-only messaging
- Emails over 160 words
- Generic openers like "Hey there!"
{{/unless}}`,
    tips: [
      "Paste each email into Klaviyo, Shopify Email or Mailchimp as a flow; use the summary table to set the delays.",
      "Follow up: “Write SMS versions of emails 1 and 5 under 160 characters each.”",
    ],
  },
];
